import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { notificationService as service } from "../../services/api";
import toast from "react-hot-toast";

const domains = [
  "work",
  "family",
  "health",
  "finance",
  "maintenance",
  "travel",
  "learning",
  "leisure",
];
export const deliveryDefaults = {
  transport: "smtp",
  domains,
  minimum_priority: "low",
  digest_interval_minutes: 30,
  critical_bypass_quiet: true,
  preview_mode: "summary",
};
const statusLabels = {
  queued: "Queued",
  preparing: "Preparing email",
  sending: "Submitting email",
  accepted: "Accepted by email server",
  retry_wait: "Safe retry scheduled",
  failed: "Needs attention",
  unknown: "Submission uncertain",
  cancelled: "Cancelled before submission",
  dismissed: "Future attempts stopped",
  legacy_unconfirmed: "Previous attempt unconfirmed",
};
const display = (value, zone) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        timeZone: zone,
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "Not recorded";
const errorText = (error) => {
  const detail = error.response?.data?.detail;
  return typeof detail === "string"
    ? detail
    : Array.isArray(detail)
      ? detail.map((item) => item.msg).join("; ")
      : "Could not update delivery. Refresh and try again.";
};

export function DeliverySettings({ value, onChange, runtime = {}, onGmailSelection = () => {}, disabled = false }) {
  const form = { ...deliveryDefaults, ...(value || {}) };
  const set = (key, val) => onChange({ ...form, [key]: val });
  const sender = useMutation({ mutationFn: service.gmailSender, onSuccess: (result) => onGmailSelection(result.selection) });
  return (
    <fieldset className="space-y-4 border-t border-[#dce3dd] pt-5">
      <legend className="font-bold text-sm pt-3">
        Reminder email preferences
      </legend>
      <label className="block text-sm font-semibold">
        Send reminder emails through
        <select className="field-input mt-1" disabled={disabled || sender.isPending} value={form.transport} onChange={(event) => set("transport", event.target.value)}>
          <option value="smtp">Server email (SMTP)</option>
          <option value="gmail">My connected Gmail account (HTTPS)</option>
        </select>
      </label>
      {form.transport === "gmail" && (
        <div className="space-y-2 text-sm">
          <p>Send reminders from your verified Gmail account to {runtime.recipient || "your Timelyzer account email"}. Gmail read and send permissions are required to verify the sender and send digests.</p>
          <button type="button" className="secondary-button" disabled={disabled || sender.isPending} onClick={() => sender.mutate()}>
            {sender.isPending ? "Checking Gmail sender…" : "Verify connected Gmail sender"}
          </button>
          {(sender.data?.mailbox || (runtime.transport === "gmail" && runtime.sender)) && (
            <p>Gmail sender: <strong>{sender.data?.mailbox || runtime.sender}</strong>. {sender.data ? "Save preferences to use this account." : "Verify again if you reconnect Google."}</p>
          )}
          {sender.error && <p role="alert" className="text-red-700">{errorText(sender.error)}</p>}
          <Link className="inline-block underline" to="/integrations">Manage Google connection</Link>
          <p className="text-xs text-[#66716a]">Choosing Gmail does not turn on reminder emails. Background checks and email reminders must also be enabled. The backend must be running to send; free hosting can sleep.</p>
        </div>
      )}
      <label className="block text-sm font-semibold">
        Email frequency (minutes)
        <input
          className="field-input mt-1"
          type="number"
          required
          min={5}
          max={1440}
          value={form.digest_interval_minutes}
          onChange={(e) =>
            set("digest_interval_minutes", Number(e.target.value))
          }
        />
      </label>
      <p className="text-xs text-[#66716a]">
        New reminders are grouped into digests of up to 20. Critical alerts can
        send sooner. Ready briefings also send sooner if waiting would miss their
        needed-by time or expiry; quiet hours and category filters still apply.
        Safe retries follow their own backoff.
      </p>
      <label className="block text-sm font-semibold">
        Lowest priority to email
        <select
          className="field-input mt-1"
          value={form.minimum_priority}
          onChange={(e) => set("minimum_priority", e.target.value)}
        >
          {["critical", "high", "normal", "low"].map((k) => (
            <option key={k}>{k}</option>
          ))}
        </select>
      </label>
      <fieldset>
        <legend className="text-sm font-semibold mb-2">
          Categories to email
        </legend>
        <div className="grid grid-cols-2 gap-2">
          {domains.map((domain) => (
            <label key={domain} className="flex gap-2 text-sm capitalize">
              <input
                type="checkbox"
                checked={form.domains.includes(domain)}
                onChange={(e) =>
                  set(
                    "domains",
                    e.target.checked
                      ? [...form.domains, domain]
                      : form.domains.filter((d) => d !== domain),
                  )
                }
              />
              {domain}
            </label>
          ))}
        </div>
      </fieldset>
      <label className="flex gap-3 text-sm">
        <input
          type="checkbox"
          checked={form.critical_bypass_quiet}
          onChange={(e) => set("critical_bypass_quiet", e.target.checked)}
        />
        Allow critical reminder emails during quiet hours
      </label>
      <label className="block text-sm font-semibold">
        Email content
        <select
          className="field-input mt-1"
          value={form.preview_mode}
          onChange={(e) => set("preview_mode", e.target.value)}
        >
          <option value="summary">Count and app link only</option>
          <option value="details">Include reminder titles and details</option>
        </select>
      </label>
      <p className="text-xs text-[#66716a]">
        Detailed emails may contain family, health, financial or work
        information from the selected categories, plus ready research briefings
        and their source links. Reminders remain in the app
        whichever email content you choose.
      </p>
    </fieldset>
  );
}

function DecisionEditor({ row, recipient, onConfirm, onCancel, busy, error }) {
  const [checked, setChecked] = useState(false);
  const uncertain = ["unknown", "legacy_unconfirmed"].includes(row.status);
  return (
    <section
      className="mt-4 rounded-md border border-[#dce3dd] bg-[#f4f8f5] p-4 space-y-3"
      aria-label="Review reminder retry"
    >
      <h4 className="font-bold text-sm">Queue another email attempt</h4>
      <p className="text-sm">
        Recipient: <strong>{recipient}</strong>. Current categories, quiet hours
        and reminder state will be checked before sending.
      </p>
      {uncertain && (
        <label className="flex gap-2 text-sm">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          I checked my mailbox and understand another attempt could produce a
          duplicate.
        </label>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {errorText(error)}
        </p>
      )}
      <div className="flex gap-3">
        <button
          className="primary-button text-xs"
          disabled={busy || (uncertain && !checked)}
          onClick={() =>
            onConfirm({
              action: "retry",
              expected_revision: row.revision,
              expected_recipient: recipient,
              acknowledge_duplicate_risk: checked,
            })
          }
        >
          Queue retry
        </button>
        <button
          className="secondary-button text-xs"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </section>
  );
}

function ReminderInbox({ timezone, refresh }) {
  const [filter, setFilter] = useState("unread"),
    [offset, setOffset] = useState(0);
  const query = useQuery({
    queryKey: ["notification-inbox", filter, offset],
    queryFn: () => service.inbox(filter, offset),
    refetchInterval: 60000,
  });
  const read = useMutation({
    mutationFn: service.read,
    onSuccess: refresh,
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <section className="surface-panel overflow-hidden">
      <div className="flex justify-between items-center gap-3 border-b p-5">
        <div>
          <h2 className="font-bold">Reminder inbox</h2>
          <p className="text-xs text-[#66716a] mt-1">
            Marking a reminder read stops its pending email. It does not
            complete the task.
          </p>
        </div>
        <select
          aria-label="Reminder status"
          className="field-input w-auto"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setOffset(0);
          }}
        >
          {["unread", "read", "resolved", "all"].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </div>
      {query.isLoading ? (
        <p role="status" className="p-5">
          Loading reminders…
        </p>
      ) : query.error ? (
        <p role="alert" className="p-5">
          Reminders unavailable.{" "}
          <button className="secondary-button" onClick={() => query.refetch()}>
            Retry inbox
          </button>
        </p>
      ) : query.data.items.length ? (
        query.data.items.map((item) => (
          <article key={item.id} className="border-b p-5 last:border-0">
            <div className="flex justify-between gap-3">
              <Link className="font-bold text-sm hover:underline" to={item.url}>
                {item.title}
              </Link>
              <span className="status-pill self-start shrink-0 status-pill-info">
                {item.priority}
              </span>
            </div>
            <p className="mt-2 text-sm text-[#66716a]">{item.reason}</p>
            <p className="mt-2 text-xs text-[#66716a]">
              {display(item.created_at, timezone)} · {item.status}
            </p>
            {item.status === "unread" && (
              <button
                className="secondary-button text-xs mt-3"
                disabled={read.isPending}
                onClick={() => read.mutate(item.id)}
              >
                Mark {item.title} read
              </button>
            )}
          </article>
        ))
      ) : (
        <p className="p-5 text-sm text-[#66716a]">
          No {filter === "all" ? "" : filter} reminders.
        </p>
      )}
      <div className="flex gap-2 p-4">
        {offset > 0 && (
          <button
            className="secondary-button text-xs"
            onClick={() => setOffset(Math.max(0, offset - 30))}
          >
            Newer reminders
          </button>
        )}
        {query.data?.next_offset != null && (
          <button
            className="secondary-button text-xs"
            onClick={() => setOffset(query.data.next_offset)}
          >
            Older reminders
          </button>
        )}
      </div>
    </section>
  );
}

export default function NotificationDesk() {
  const client = useQueryClient(),
    [offset, setOffset] = useState(0),
    [review, setReview] = useState(null);
  const query = useQuery({
    queryKey: ["notification-delivery", offset],
    queryFn: () => service.list(offset),
    refetchInterval: 30000,
  });
  const refresh = () =>
    [
      "notification-delivery",
      "notification-inbox",
      "assistant",
      "today",
    ].forEach((k) => client.invalidateQueries({ queryKey: [k] }));
  const mutation = useMutation({
    mutationFn: ({ row, decision }) =>
      row.status === "legacy_unconfirmed"
        ? service.legacy(row.id, decision)
        : service.decide(row.id, decision),
    onSuccess: () => {
      setReview(null);
      refresh();
      toast.success("Delivery decision saved");
    },
  });
  if (query.isLoading)
    return <p role="status">Loading notification delivery…</p>;
  if (query.error)
    return (
      <p role="alert">
        Delivery history unavailable.{" "}
        <button className="secondary-button" onClick={() => query.refetch()}>
          Retry delivery history
        </button>
      </p>
    );
  const data = query.data,
    runtime = data.runtime;
  const openReview = (row) => {
    mutation.reset();
    setReview({ ...row, account_email: data.account_email });
  };
  const decisions = (row) => (
    <>
      {row.can_retry && (
        <button
          className="secondary-button text-xs"
          disabled={!!review || mutation.isPending}
          onClick={() => openReview(row)}
        >
          Review retry
        </button>
      )}
      {row.can_dismiss && (
        <button
          className="quiet-button text-xs"
          disabled={mutation.isPending || !!review}
          onClick={() =>
            mutation.mutate({
              row,
              decision: { action: "dismiss", expected_revision: row.revision },
            })
          }
        >
          Stop future attempts
        </button>
      )}
    </>
  );
  return (
    <div className="space-y-5">
      <header className="flex flex-wrap justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">Notifications and delivery</h2>
          <p className="text-sm text-[#66716a] mt-1">
            See what needs attention and what happened to each reminder email.
          </p>
        </div>
        <Link className="secondary-button" to="/assistant?view=settings">
          Notification preferences
        </Link>
      </header>
      <section
        className="surface-panel p-5 space-y-2"
        aria-label="Delivery status"
      >
        <p className="text-sm">
          <strong>{data.pending_count}</strong> reminders waiting for an
          eligible email digest · {data.account_email}
        </p>
        {!runtime.worker_enabled ? (
          <p className="text-sm text-amber-800">
            Background workers are disabled on this server. Reminders cannot be
            sent automatically.
          </p>
        ) : !runtime.enabled || !runtime.email_notifications ? (
          <p className="text-sm text-[#66716a]">
            Reminder emails are off. Enable background checks and email
            reminders in preferences.
          </p>
        ) : !runtime.email_configured ? (
          <p className="text-sm text-amber-800">
            {runtime.email_issue || "SMTP is not configured. Your reminders stay queued in the app."}
          </p>
        ) : runtime.check_stale ? (
          <p role="alert" className="text-sm text-amber-800">
            No recent delivery check is recorded. Check that the backend worker
            stays running.
          </p>
        ) : (
          <p className="text-sm text-[#246b52]">
            Delivery worker checked recently.
          </p>
        )}
        <p className="text-xs text-[#66716a]">
          Channel: {runtime.transport === "gmail" ? `Gmail API${runtime.sender ? ` (${runtime.sender})` : ""}` : "Server email (SMTP)"}.{" "}
          Last delivery check: {display(runtime.last_checked_at, data.timezone)}{" "}
          ({data.timezone}). Email mode:{" "}
          {data.preferences.preview_mode === "details"
            ? "titles and details"
            : "count and app link only"}
          .
        </p>
        <p className="text-xs text-[#66716a]">
          Email-server acceptance does not confirm arrival in your inbox.
          Uncertain submissions stop for review.
        </p>
      </section>
      {mutation.error && !review && (
        <p role="alert" className="text-sm text-red-700">
          {errorText(mutation.error)}
        </p>
      )}
      <div className="grid gap-5 xl:grid-cols-2">
        <ReminderInbox timezone={data.timezone} refresh={refresh} />
        <section className="space-y-3">
          <h2 className="text-lg font-bold">Email delivery history</h2>
          {data.items.length === 0 && (
            <p className="surface-panel p-5 text-sm text-[#66716a]">
              No digest attempts yet. Eligible unread reminders are grouped when
              your preferences and delivery configuration allow.
            </p>
          )}
          {data.items.map((row) => (
            <article key={row.id} className="surface-panel p-5">
              <div className="flex justify-between gap-3">
                <h3 className="text-sm font-bold">{row.subject}</h3>
                <span
                  className={`status-pill self-start shrink-0 ${row.status === "accepted" ? "status-pill-success" : ["failed", "unknown"].includes(row.status) ? "status-pill-warning" : "status-pill-info"}`}
                >
                  {statusLabels[row.status] || row.status}
                </span>
              </div>
              <p className="mt-2 text-xs text-[#66716a]">
                To {row.recipient} · {row.attempt_count}{" "}
                {row.attempt_count === 1 ? "attempt" : "attempts"} ·{" "}
                {display(row.created_at, data.timezone)}
              </p>
              <p className="mt-2 text-xs text-[#66716a]">
                {row.transport === "gmail" ? "Gmail API" : "Server email (SMTP)"}{row.sender ? ` · From ${row.sender}` : ""}
                {row.provider_receipt?.message_id ? ` · Gmail receipt: ${row.provider_receipt.message_id}` : ""}
              </p>
              {row.error && <p className="mt-3 text-sm">{row.error}</p>}
              {row.accepted_at && (
                <p className="mt-2 text-xs text-[#66716a]">
                  Accepted {display(row.accepted_at, data.timezone)}. Inbox
                  delivery is unconfirmed.
                </p>
              )}
              {row.status === "unknown" &&
                !row.can_retry &&
                row.review_available_at && (
                  <p className="mt-2 text-xs text-[#66716a]">
                    Review becomes available after{" "}
                    {display(row.review_available_at, data.timezone)} while the
                    submission lease ends.
                  </p>
                )}
              {row.next_attempt_at && (
                <p className="mt-2 text-xs text-[#66716a]">
                  Next eligible attempt:{" "}
                  {display(row.next_attempt_at, data.timezone)}
                </p>
              )}
              <details className="mt-3 text-sm">
                <summary className="cursor-pointer font-semibold">
                  Reminders and attempt history
                </summary>
                <p className="text-xs mt-2 text-[#66716a]">
                  {row.preview_mode === "summary"
                    ? "Email contains a count and app link. Private reminder details are shown here in the app."
                    : "Reminder titles and details are included in the email."}
                </p>
                <ul className="mt-3 space-y-2">
                  {row.items.map((item) => (
                    <li key={item.id}>
                      <Link className="underline" to={item.url}>
                        {item.title}
                      </Link>
                      <p className="text-xs text-[#66716a]">{item.reason}</p>
                    </li>
                  ))}
                </ul>
                {!row.items.length && (
                  <p className="text-xs mt-2">
                    No reminder records remain attached to this receipt.
                  </p>
                )}
                <ol className="text-xs mt-3 space-y-1">
                  {row.attempts.map((attempt, index) => (
                    <li key={index}>
                      {display(attempt.at, data.timezone)} ·{" "}
                      {statusLabels[attempt.status] ||
                        attempt.status.replaceAll("_", " ")}
                      {attempt.message ? ` · ${attempt.message}` : ""}
                    </li>
                  ))}
                </ol>
                <p className="mt-3 text-xs break-all text-[#66716a]">
                  Message ID: {row.message_id}
                </p>
              </details>
              <div className="flex gap-3 mt-4">{decisions(row)}</div>
              {review?.id === row.id &&
                review.status !== "legacy_unconfirmed" && (
                  <DecisionEditor
                    key={row.id}
                    row={review}
                    recipient={review.account_email}
                    busy={mutation.isPending}
                    error={mutation.error}
                    onCancel={() => {
                      setReview(null);
                      mutation.reset();
                    }}
                    onConfirm={(decision) =>
                      mutation.mutate({ row: review, decision })
                    }
                  />
                )}
            </article>
          ))}
          <div className="flex gap-3">
            {offset > 0 && (
              <button
                className="secondary-button text-xs"
                onClick={() => setOffset(Math.max(0, offset - 20))}
              >
                Newer deliveries
              </button>
            )}
            {data.next_offset != null && (
              <button
                className="secondary-button text-xs"
                onClick={() => setOffset(data.next_offset)}
              >
                Older deliveries
              </button>
            )}
          </div>
          {data.legacy.length > 0 && (
            <section className="surface-panel p-5 space-y-3">
              <h3 className="font-bold">Previous attempts without receipts</h3>
              <p className="text-sm text-[#66716a]">
                Older reminders were marked attempted without recording an SMTP
                response. Check your mailbox before requesting another copy.
              </p>
              {data.legacy.map((row) => (
                <div key={row.id} className="border-t pt-3">
                  <p className="text-sm font-semibold">{row.title}</p>
                  <div className="flex gap-3 mt-2">
                    {decisions({ ...row, can_retry: true, can_dismiss: true })}
                  </div>
                  {review?.id === row.id &&
                    review.status === "legacy_unconfirmed" && (
                      <DecisionEditor
                        row={review}
                        recipient={review.account_email}
                        busy={mutation.isPending}
                        error={mutation.error}
                        onCancel={() => {
                          setReview(null);
                          mutation.reset();
                        }}
                        onConfirm={(decision) =>
                          mutation.mutate({ row: review, decision })
                        }
                      />
                    )}
                </div>
              ))}
              {data.more_legacy && (
                <p className="text-xs">
                  More previous attempts will appear after these are reviewed.
                </p>
              )}
            </section>
          )}
        </section>
      </div>
    </div>
  );
}
